import type { ImportMasterFilledFieldDto, ImportTimeBackfilledFieldDto } from "@carry/contract";
import { INITIAL_ITEM } from "./constants";
import type { DraftOrder } from "./types";

/**
 * PDF 解析で取得した支払区分の文字列をドラフト値へ整える。
 * 支払区分は自由テキストのため丸めず、前後の空白のみ落として原文を採る
 * (「Cqree売掛」「一括請求（後日請求）」等、取引先ごとの実表記を保つ)。
 */
function paymentMethodText(raw: string | null): string {
  return raw?.trim() ?? "";
}

export interface ParsedOrderItemPreview {
  product_name: string;
  quantity: number;
  unit_price: number | null;
}

export interface ParsedMasterSuggestion {
  public_id: string;
  score: number;
}

export interface ParsedOrderPreview {
  confidence?: number;
  confidence_reasons?: string[];
  order_number: string | null;
  payment_number: string | null;
  delivery_date: string;
  delivery_time_from: string | null;
  delivery_time_to: string | null;
  delivery_address: string;
  delivery_lat?: number | null;
  delivery_lng?: number | null;
  facility_name: string | null;
  handover_location: string | null;
  arrival_notes: string | null;
  delivery_name: string | null;
  delivery_phone: string | null;
  delivery_email: string | null;
  delivery_company: string | null;
  delivery_branch: string | null;
  delivery_name_kana: string | null;
  payment_method: string | null;
  items: ParsedOrderItemPreview[];
  pickup_store_name: string | null;
  pickup_address: string | null;
  pickup_lat?: number | null;
  pickup_lng?: number | null;
  pickup_phone: string | null;
  /** 集荷の指定時刻 (#657)。片側だけ抽出できた場合は取込時に許容分で補完済み。 */
  pickup_time_start?: string | null;
  pickup_time_end?: string | null;
  packaging: string | null;
  store_message: string | null;
  delivery_request: string | null;
  remarks: string | null;
  uploaded_file_public_id: string | null;
  suggested_pickup_spot: ParsedMasterSuggestion | null;
  suggested_delivery_spot: ParsedMasterSuggestion | null;
  suggested_client: ParsedMasterSuggestion | null;
  /** 注文書から取れず取引先マスタの既定値で補完した項目 (Issue #613)。 */
  master_filled_fields?: readonly ImportMasterFilledFieldDto[];
  /** 片側しか読み取れず取込時に許容分で埋めた時刻の項目 (Issue #657)。 */
  time_backfilled_fields?: readonly ImportTimeBackfilledFieldDto[];
}

export interface DraftOrderFieldWriter {
  <TField extends keyof DraftOrder>(field: TField, value: DraftOrder[TField]): void;
}

export type ImportedFieldBadges = Partial<
  Record<keyof DraftOrder, "PDF反映" | "要確認" | "マスタ補完" | "自動補完">
>;

export function buildImportedFieldBadges(p: ParsedOrderPreview): ImportedFieldBadges {
  const badges: ImportedFieldBadges = {};
  const mark = (field: keyof DraftOrder, value: unknown) => {
    if (Array.isArray(value)) {
      if (value.length > 0) badges[field] = "PDF反映";
      return;
    }
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      badges[field] = "PDF反映";
    }
  };

  mark("order_number", p.order_number);
  mark("payment_number", p.payment_number);
  mark("payment_type", paymentMethodText(p.payment_method));
  mark("delivery_date", p.delivery_date);
  mark("delivery_time_start", p.delivery_time_from);
  mark("delivery_time_end", p.delivery_time_to);
  mark("delivery_address", p.delivery_address);
  mark("delivery_building", p.facility_name);
  mark("handover_location", p.handover_location);
  mark("arrival_instructions", p.arrival_notes);
  mark("delivery_company", p.delivery_company);
  mark("delivery_branch", p.delivery_branch);
  mark("delivery_person_name", p.delivery_name);
  mark("delivery_person_name_kana", p.delivery_name_kana);
  mark("delivery_email", p.delivery_email);
  mark("delivery_phone", p.delivery_phone);
  mark("store_name", p.pickup_store_name);
  mark("store_address", p.pickup_address);
  mark("store_phone", p.pickup_phone);
  mark("pickup_time_start", p.pickup_time_start);
  mark("pickup_time_end", p.pickup_time_end);
  mark("delivery_packaging", p.packaging);
  mark("store_message", p.store_message);
  mark("delivery_notes", p.delivery_request);
  mark("remarks", p.remarks);
  mark("items", p.items);

  // 取引先マスタで補完した項目は「PDF反映」と区別して補完元が分かるようにする (Issue #613)。
  if (p.master_filled_fields?.includes("pickup_address")) badges.store_address = "マスタ補完";

  // 片側しか読み取れず許容分で埋めた時刻も「PDF反映」と区別する (Issue #657)。
  // 注文書に書かれていない値を「注文書どおり」と誤読させないため、mark の後に上書きする。
  for (const field of p.time_backfilled_fields ?? []) badges[field] = "自動補完";

  if (!p.order_number) badges.order_number = "要確認";
  if (!p.delivery_address) badges.delivery_address = "要確認";
  if (!p.delivery_company) badges.delivery_company = "要確認";
  if (!p.pickup_store_name) badges.store_name = "要確認";
  if (p.items.length === 0) badges.items = "要確認";
  // 納品先・取引先・集荷先マスタの紐付けは「要確認」対象としない (PDF 取込時に確認不要)

  return badges;
}

export function parsedPreviewToDraft(p: ParsedOrderPreview): DraftOrder {
  return {
    uploaded_file_public_id: p.uploaded_file_public_id,
    delivery_spot_public_id: p.suggested_delivery_spot?.public_id ?? null,
    client_id: p.suggested_client?.public_id ?? null,
    pickup_spot_public_id: p.suggested_pickup_spot?.public_id ?? null,
    order_number: p.order_number ?? "",
    payment_number: p.payment_number ?? "",
    payment_type: paymentMethodText(p.payment_method),
    delivery_date: p.delivery_date,
    delivery_time_start: p.delivery_time_from ?? "",
    delivery_time_end: p.delivery_time_to ?? "",
    delivery_address: p.delivery_address,
    delivery_building: p.facility_name ?? "",
    handover_location: p.handover_location ?? "",
    arrival_instructions: p.arrival_notes ?? "",
    delivery_company: p.delivery_company ?? "",
    delivery_branch: p.delivery_branch ?? "",
    delivery_person_name: p.delivery_name ?? "",
    delivery_person_name_kana: p.delivery_name_kana ?? "",
    delivery_email: p.delivery_email ?? "",
    delivery_phone: p.delivery_phone ?? "",
    store_name: p.pickup_store_name ?? "",
    store_address: p.pickup_address ?? "",
    store_phone: p.pickup_phone ?? "",
    pickup_time_start: p.pickup_time_start ?? "",
    pickup_time_end: p.pickup_time_end ?? "",
    pickup_unavailable_times: [],
    pickup_duration_minutes: null,
    // 注文書に経由地は書かれていないため、取込は常に経由地なしで始める (#723)。
    // 既存フォームへの反映 (applyDraftToOrderForm) でも waypoints は上書きしない。
    pickup_sequence: 1,
    waypoints: [],
    delivery_packaging: p.packaging ?? "",
    store_message: p.store_message ?? "",
    delivery_notes: p.delivery_request ?? "",
    day_of_contact_person: "",
    day_of_contact_phone: "",
    delivery_duration_minutes: null,
    delivery_unavailable_times: [],
    remarks: p.remarks ?? "",
    // #767: 注文書からは 3 時間 / 5 時間を判別できないので、取込は常に標準 (null) で入る。
    // 延長が要る案件は受注確定前に人が切り替える。applyDraftToOrderForm でも上書きしない
    // (経由地と同じ理由 — 再取込のたびに手で入れた延長が消えるのを防ぐ)。
    max_ride_minutes: null,
    items:
      p.items.length > 0
        ? p.items.map((it) => ({
            product_name: it.product_name,
            quantity: it.quantity,
            unit_price: it.unit_price,
          }))
        : [{ ...INITIAL_ITEM }],
    status: "draft",
  };
}

export function applyDraftToOrderForm(
  setFieldValue: DraftOrderFieldWriter,
  draft: DraftOrder,
): void {
  setFieldValue("uploaded_file_public_id", draft.uploaded_file_public_id);
  setFieldValue("delivery_spot_public_id", draft.delivery_spot_public_id);
  setFieldValue("client_id", draft.client_id);
  setFieldValue("pickup_spot_public_id", draft.pickup_spot_public_id);
  setFieldValue("order_number", draft.order_number);
  setFieldValue("payment_number", draft.payment_number);
  setFieldValue("payment_type", draft.payment_type);
  setFieldValue("delivery_date", draft.delivery_date);
  setFieldValue("delivery_time_start", draft.delivery_time_start);
  setFieldValue("delivery_time_end", draft.delivery_time_end);
  setFieldValue("delivery_address", draft.delivery_address);
  setFieldValue("delivery_building", draft.delivery_building);
  setFieldValue("handover_location", draft.handover_location);
  setFieldValue("arrival_instructions", draft.arrival_instructions);
  setFieldValue("delivery_company", draft.delivery_company);
  setFieldValue("delivery_branch", draft.delivery_branch);
  setFieldValue("delivery_person_name", draft.delivery_person_name);
  setFieldValue("delivery_person_name_kana", draft.delivery_person_name_kana);
  setFieldValue("delivery_email", draft.delivery_email);
  setFieldValue("delivery_phone", draft.delivery_phone);
  setFieldValue("store_name", draft.store_name);
  setFieldValue("store_address", draft.store_address);
  setFieldValue("store_phone", draft.store_phone);
  setFieldValue("pickup_time_start", draft.pickup_time_start);
  setFieldValue("pickup_time_end", draft.pickup_time_end);
  setFieldValue("pickup_unavailable_times", draft.pickup_unavailable_times);
  setFieldValue("pickup_duration_minutes", draft.pickup_duration_minutes);
  setFieldValue("delivery_packaging", draft.delivery_packaging);
  setFieldValue("store_message", draft.store_message);
  setFieldValue("delivery_notes", draft.delivery_notes);
  setFieldValue("day_of_contact_person", draft.day_of_contact_person);
  setFieldValue("day_of_contact_phone", draft.day_of_contact_phone);
  setFieldValue("delivery_duration_minutes", draft.delivery_duration_minutes);
  setFieldValue("remarks", draft.remarks);
  setFieldValue("items", draft.items);
  setFieldValue("status", draft.status);
}

export function buildModifiedFields(
  currentValues: DraftOrder,
  importedValues: Partial<DraftOrder> | null,
  badges: ImportedFieldBadges,
): Set<keyof DraftOrder> {
  const result = new Set<keyof DraftOrder>();
  if (!importedValues) return result;
  for (const rawKey of Object.keys(badges)) {
    const key = rawKey as keyof DraftOrder;
    if (key === "items") continue;
    const importedVal = importedValues[key];
    if (importedVal !== undefined && currentValues[key] !== importedVal) {
      result.add(key);
    }
  }
  return result;
}
